/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiToolTip } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useGetOrganizationQuery, useProfile } from '@modules/profile-lib/hooks'
import type { RoutableBreadcrumb } from '@modules/ui-types'

import { useConfig } from '../../../ConfigContext'
import OrganizationPopover from '../OrganizationPopover'

const useUserOrganizationBreadcrumb = (): RoutableBreadcrumb => {
  const [_, { multiOrgAuth: hasMultiOrgAuthFlag }] = useFlagsWhenLoaded()
  const isUserConsole = useConfig('APP_NAME') === 'userconsole'
  const profile = useProfile({ enabled: isUserConsole })
  const organizationId = profile?.organization_id
  const { data: organizationData } = useGetOrganizationQuery(organizationId, {
    enabled: !!organizationId && isUserConsole,
  })
  const organizationName = organizationData?.organization.organization_name

  return {
    text: (
      <EuiToolTip
        position='right'
        content={
          organizationName ? (
            <FormattedMessage
              id='organization-switch.tooltip'
              defaultMessage="You're currently logged in to{lineBreak}{organizationName}"
              values={{
                organizationName,
                lineBreak: <br />,
              }}
            />
          ) : (
            <FormattedMessage
              id='organization-switch.breadcrumb.no-organization-tooltip'
              defaultMessage="You're not logged in to any organizations."
            />
          )
        }
      >
        <span
          tabIndex={0}
          style={{
            textOverflow: 'ellipsis',
            maxWidth: '100px',
            overflow: 'hidden',
            display: 'inline-block',
            verticalAlign: 'middle',
            textDecoration: hasMultiOrgAuthFlag ? 'underline' : 'none',
          }}
        >
          {organizationName ? (
            organizationName
          ) : (
            <FormattedMessage
              id='organization-switch.breadcrumb.no-organization'
              defaultMessage='...'
            />
          )}
        </span>
      </EuiToolTip>
    ),
    popoverContent: hasMultiOrgAuthFlag
      ? (closePopover) => <OrganizationPopover closePopover={closePopover} />
      : undefined,
    popoverProps: {
      hasArrow: false,
      anchorPosition: 'downRight',
      offset: 12,
      panelPaddingSize: 'none',
      repositionOnScroll: true,
      repositionToCrossAxis: true,
    },
  }
}

export default useUserOrganizationBreadcrumb
